/**
 * Leveranciersbeoordelingen (PL) — beoordeel de freelancers waarmee je afgelopen
 * kwartaal werkte. Leest de snapshot uit de AI Engine (/pending) en schrijft per
 * beoordeling terug naar Airtable. Zes sterren-criteria + conclusie per freelancer.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Rating, TextField, Button, Alert,
  CircularProgress, Stack, Divider, Chip,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import PageHeader from '../../ui-components/PageHeader';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { bevestig } from '../../utils/bevestig';

// Exact de Airtable-veldnamen die de engine verwacht (RATING_FIELDS).
const CRITERIA = [
  { key: 'Flexibiliteit', label: 'Flexibiliteit' },
  { key: 'Kwaliteit', label: 'Kwaliteit' },
  { key: 'Bereikbaarheid', label: 'Bereikbaarheid' },
  { key: 'Naleving afspraken', label: 'Naleving afspraken' },
  { key: 'Communicatie', label: 'Communicatie' },
  { key: 'Overall beoordeling', label: 'Overall beoordeling' },
];

export default function LeveranciersBeoordelingen() {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null); // laadfout → lijst verbergen + retry
  const [error, setError] = useState(null);         // actiefout (opslaan/validatie) → lijst blijft
  const [ok, setOk] = useState(null);
  const [quarter, setQuarter] = useState(null);
  const [items, setItems] = useState([]);
  const [forms, setForms] = useState({}); // id -> { ratings:{}, conclusie:'', busy:false }

  const load = useCallback(async () => {
    setLoading(true); setLoadError(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/leverancier-reviews/pending`);
      if (!r.ok) throw new Error(`Laden mislukt (${r.status})`);
      const d = await r.json();
      setQuarter(d.quarter_id || null);
      setItems(Array.isArray(d.items) ? d.items : []);
    } catch (e) {
      setLoadError(e.message || 'Kon de beoordelingen niet laden — probeer het zo nog eens.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setRating = (id, key, val) => setForms((p) => ({
    ...p, [id]: { ...(p[id] || { ratings: {}, conclusie: '' }), ratings: { ...((p[id] || {}).ratings || {}), [key]: val } },
  }));
  const setConclusie = (id, val) => setForms((p) => ({
    ...p, [id]: { ...(p[id] || { ratings: {}, conclusie: '' }), conclusie: val },
  }));

  const submit = async (item) => {
    const form = forms[item.id] || { ratings: {}, conclusie: '' };
    const ratings = form.ratings || {};
    if (!CRITERIA.every((c) => ratings[c.key] >= 1)) {
      setError('Vul alle zes de sterren-criteria in voordat je opslaat.');
      return;
    }
    if (!(await bevestig({ titel: 'Beoordeling indienen?', tekst: 'Daarna kun je hem niet meer aanpassen.', knop: 'Indienen' }))) return;
    setForms((p) => ({ ...p, [item.id]: { ...form, busy: true } }));
    setError(null); setOk(null);
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/leverancier-reviews/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: item.id, ratings, conclusie: form.conclusie || '' }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `Opslaan mislukt (${r.status})`);
      setItems((list) => list.filter((x) => x.id !== item.id));
      setOk(`Beoordeling van ${item.freelancer_name || 'freelancer'} opgeslagen.`);
    } catch (e) {
      setError(e.message || 'Opslaan mislukt.');
      setForms((p) => ({ ...p, [item.id]: { ...form, busy: false } }));
    }
  };

  const quarterLabel = useMemo(() => {
    if (!quarter) return '';
    const [y, q] = String(quarter).split('-Q');
    return `Q${q} ${y}`;
  }, [quarter]);

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, md: 3 }, py: 3 }}>
      <PageHeader
        icon="star"
        title="Leveranciersbeoordelingen"
        subtitle="Beoordeel de freelancers waarmee je het afgelopen kwartaal werkte. Zo houden we de kwaliteit van onze leveranciers scherp."
      />

      {quarter && (
        <Chip icon={<StarIcon fontSize="small" />} label={`Ronde: ${quarterLabel}`}
          sx={{ mb: 2, bgcolor: '#eef2ff', color: '#3730a3', fontWeight: 600 }} />
      )}

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 700 }}>Opnieuw</Button>}>
          {loadError}
        </Alert>
      )}
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {ok && <Alert severity="success" onClose={() => setOk(null)} sx={{ mb: 2 }}>{ok}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : loadError ? null : items.length === 0 ? (
        <Alert severity="success">
          Je hebt geen openstaande beoordelingen{quarter ? ` voor ${quarterLabel}` : ''}. Niets te doen — top!
        </Alert>
      ) : (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {items.length} freelancer{items.length === 1 ? '' : 's'} te beoordelen.
          </Typography>
          {items.map((item) => {
            const form = forms[item.id] || { ratings: {}, conclusie: '', busy: false };
            return (
              <Card key={item.id} variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                    {item.freelancer_name || 'Onbekende freelancer'}
                  </Typography>
                  <Stack spacing={1}>
                    {CRITERIA.map((c) => (
                      <Stack key={c.key} direction="row" alignItems="center" justifyContent="space-between"
                        sx={{ maxWidth: 420 }}>
                        <Typography variant="body2">{c.label}</Typography>
                        <Rating
                          value={form.ratings[c.key] || 0}
                          onChange={(_e, v) => setRating(item.id, c.key, v)}
                        />
                      </Stack>
                    ))}
                  </Stack>
                  <Divider sx={{ my: 2 }} />
                  <TextField
                    label="Conclusie / toelichting (optioneel)"
                    multiline minRows={2} fullWidth
                    value={form.conclusie}
                    onChange={(e) => setConclusie(item.id, e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <Button variant="contained" onClick={() => submit(item)} disabled={form.busy}
                    sx={{ textTransform: 'none', fontWeight: 700 }}>
                    {form.busy ? 'Opslaan…' : 'Beoordeling opslaan'}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
